import { computed, Injectable, signal } from '@angular/core';
import {
  Adjustment,
  ExtractionItem,
  ExtractionResult,
  flattenItems,
  rootItem,
} from '../types/extraction-result';
import { OverlayBox } from '../types/overlay';
import { PlausibilityItemInput } from '../types/plausibility';
import {
  MarkTarget,
  MetadataDraft,
  MetadataGroup,
  METADATA_GROUPS,
  PageBox,
  PositionDraft,
  POSITION_FIELDS,
  PositionField,
  ROOT_METADATA_FIELDS,
  Selection,
} from '../types/working-copy';
import { discountsToColumn, fractionToPercentText, isPercentAdjustment } from '../utils/discounts';
import {
  childCount,
  displayNumber,
  expandToSubtrees,
  hasPreviousSibling,
  nestPositions,
  normalizeDepths,
  parentIndex,
  subtreeEnd,
} from '../utils/position-tree';
import { Box, hullOfWords, textInBox, tightenToWords, wordsInBox } from '../utils/geometry';
import { findValueBox, matchWordRun, parseGermanNumber } from '../utils/word-search';
import { Word } from '../types/job';

/**
 * Editable working copy of the extraction result: position drafts, metadata
 * drafts, the row/field selection driving the document overlay, and the input
 * assembly for the plausibility check. The pristine result stays untouched —
 * the JSON dialog shows it verbatim.
 *
 * Field edits MUTATE the drafts (see working-copy.ts on why); structural
 * changes go through the signals, `revision` re-triggers overlay computation
 * for out-of-band box changes.
 */
@Injectable()
export class ExtractionEditService {
  private result: ExtractionResult | undefined;
  private words: Word[] = [];
  private newFieldSequence = 0;
  private draftSequence = 0;

  private readonly _positions = signal<PositionDraft[]>([]);
  private readonly _metadata = signal<MetadataDraft[]>([]);
  private readonly _selection = signal<Selection>(null);
  private readonly _markTarget = signal<MarkTarget | null>(null);
  private readonly _revision = signal(0);

  readonly positions = this._positions.asReadonly();
  readonly metadata = this._metadata.asReadonly();
  readonly selection = this._selection.asReadonly();
  readonly markTarget = this._markTarget.asReadonly();
  /** Bumped on every out-of-band draft mutation (field edits mutate in place). */
  readonly revision = this._revision.asReadonly();

  /** Supplier as currently edited — scopes the supplier-number product match. */
  readonly supplierName = computed(() => {
    this._revision();
    return (
      this._metadata()
        .find((entry) => entry.key === 'metadata.supplier.name')
        ?.value?.trim() || null
    );
  });

  /** Boxes to highlight on the document, derived from the selection. */
  readonly overlayBoxes = computed<OverlayBox[]>(() => {
    this._revision();
    const selection = this._selection();
    if (!selection) return [];
    const positions = this._positions();
    if (selection.kind === 'position-field') {
      const box = this.positionFieldBox(positions[selection.index], selection.field);
      return box ? [{ ...box, kind: 'field' }] : [];
    }
    if (selection.kind === 'metadata-field') {
      const entry = this._metadata().find((e) => e.key === selection.key);
      const box = entry ? this.metadataFieldBox(entry) : null;
      return box ? [{ ...box, kind: 'field' }] : [];
    }
    return [...selection.indexes]
      .map((index) => this.rowBox(positions[index]))
      .filter((box): box is PageBox => !!box)
      .map((box) => ({ ...box, kind: 'position' as const }));
  });

  init(result: ExtractionResult, words: Word[]): void {
    this.result = result;
    this.words = words;
    this.draftSequence = 0;
    const rawValues = result.postprocess?.raw_values ?? {};
    this._positions.set(
      flattenItems(result).map(({ item, path, depth }) => ({
        id: this.nextDraftId(),
        fields: this.buildFields(item),
        // The raw-value key is the row's RESULT PATH, not its flat index.
        raws: Object.fromEntries(
          POSITION_FIELDS.map((field) => [field, rawValues[`${path}.${field}`]]).filter(
            ([, raw]) => raw != null
          )
        ),
        boxes: this.buildFieldBoxes(item),
        discounts: item.discounts,
        validation: item.validation,
        page: item.page ?? 1,
        rowbbox: item.bbox,
        depth,
      }))
    );
    this._metadata.set(this.buildMetadataDrafts(result, rawValues));
    this._selection.set(null);
    this._markTarget.set(null);
    this._revision.update((r) => r + 1);
  }

  /**
   * Word geometry arrives after the result: tighten the metadata boxes to the
   * actual value words (row-wide pipeline boxes otherwise) without touching
   * user-added fields or edits.
   */
  updateWords(words: Word[]): void {
    this.words = words;
    for (const entry of this._metadata()) {
      if (entry.isNew) continue;
      const box = findValueBox(entry.value, words);
      if (box) entry.box = box;
    }
    this._revision.update((r) => r + 1);
  }

  // ------------------------------------------------------------- selection

  /**
   * Row click: isolates the row's box on the document; clicking the sole
   * selected row again clears; additive (Ctrl/Cmd) toggles set membership.
   */
  selectRow(index: number, additive: boolean): void {
    const current = this._selection();
    if (additive) {
      const indexes = new Set(current?.kind === 'rows' ? current.indexes : []);
      if (indexes.has(index)) indexes.delete(index);
      else indexes.add(index);
      this._selection.set({ kind: 'rows', indexes });
      return;
    }
    if (
      current?.kind === 'rows' &&
      current.indexes.size === 1 &&
      current.indexes.has(index)
    ) {
      this._selection.set(null);
      return;
    }
    this._selection.set({ kind: 'rows', indexes: new Set([index]) });
  }

  /** Field click beats row selection — exactly that value's box is shown. */
  selectPositionField(index: number, field: PositionField): void {
    this._selection.set({ kind: 'position-field', index, field });
  }

  selectMetadataField(key: string): void {
    this._selection.set({ kind: 'metadata-field', key });
  }

  clearSelection(): void {
    this._selection.set(null);
  }

  // ------------------------------------------------------------- field edits

  setPositionField(index: number, field: PositionField, value: string): void {
    const draft = this._positions()[index];
    if (draft) draft.fields[field] = value;
  }

  setMetadataValue(key: string, value: string): void {
    const entry = this._metadata().find((e) => e.key === key);
    if (!entry || entry.readonly) return;
    entry.value = value;
    // the PDF info strip reads these drafts — it has no other change signal
    this._revision.update((r) => r + 1);
  }

  setNewMetadataCategory(key: string, category: string): void {
    const entry = this._metadata().find((e) => e.key === key);
    if (entry?.isNew) entry.label = category;
  }

  // ------------------------------------------------------------- structure

  /** Appends at the end by default; `at`/`depth` place the row in the tree. */
  addPosition(
    fields: Partial<Record<PositionField, string>> = {},
    options?: { at?: number; depth?: number }
  ): void {
    const draft = this.emptyDraft(fields, options?.depth ?? 0);
    const positions = [...this._positions()];
    const at = Math.max(0, Math.min(options?.at ?? positions.length, positions.length));
    positions.splice(at, 0, draft);
    this.setPositions(this.applyDepths(positions));
  }

  /** New child of `index`, appended after that row's existing subtree. */
  addSubPosition(index: number): void {
    const positions = this._positions();
    const parent = positions[index];
    if (!parent) return;
    const draft = this.emptyDraft(
      // Derive the number the row would display anyway, so a new child is
      // numbered in the document's own scheme instead of appearing unnumbered.
      { item_number: this.derivedChildNumber(positions, index) },
      parent.depth + 1
    );
    const next = [...positions];
    next.splice(subtreeEnd(positions, index), 0, draft);
    this.setPositions(next);
  }

  /** A row can only become the child of an existing previous sibling. */
  canIndent(index: number): boolean {
    return hasPreviousSibling(this._positions(), index);
  }

  indent(index: number): void {
    if (!this.canIndent(index)) return;
    this.shiftSubtreeDepth(index, 1);
  }

  canOutdent(index: number): boolean {
    return (this._positions()[index]?.depth ?? 0) > 0;
  }

  /**
   * One level up — and out to the END of the former parent's subtree, so the
   * rows that used to follow the outdented one stay that parent's children.
   */
  outdent(index: number): void {
    if (!this.canOutdent(index)) return;
    const positions = this._positions();
    const parent = parentIndex(positions, index);
    const end = subtreeEnd(positions, index);
    const moved = positions.slice(index, end);
    const rest = [...positions.slice(0, index), ...positions.slice(end)];
    // The parent shrank by the cut, so recompute its end on the REMAINING rows
    // before the depths change underneath it.
    const insertAt = parent < 0 ? rest.length : subtreeEnd(rest, parent);
    for (const draft of moved) draft.depth = Math.max(0, draft.depth - 1);
    rest.splice(insertAt, 0, ...moved);
    this.setPositions(this.applyDepths(rest));
  }

  /** Removes the row WITH its subtree — a parent never outlives its children. */
  removePosition(index: number): void {
    const positions = this._positions();
    const end = subtreeEnd(positions, index);
    this.setPositions(
      this.applyDepths([...positions.slice(0, index), ...positions.slice(end)])
    );
  }

  removeSelectedRows(): void {
    const selection = this._selection();
    if (selection?.kind !== 'rows' || !selection.indexes.size) return;
    const positions = this._positions();
    const doomed = new Set(expandToSubtrees(positions, selection.indexes));
    this.setPositions(this.applyDepths(positions.filter((_, i) => !doomed.has(i))));
  }

  /**
   * Drag-move: the dragged rows travel WITH their subtrees and land in front of
   * the target row, the moved heads adopting its depth. A drop inside one's own
   * subtree is a no-op — a row cannot become its own descendant.
   */
  reorder(dragIndexes: number[], target: number): void {
    if (!dragIndexes.length) return;
    const positions = this._positions();
    const moving = expandToSubtrees(positions, dragIndexes);
    if (moving.includes(target)) return;

    const movingSet = new Set(moving);
    // Only the heads are re-levelled; their descendants keep their offset.
    const roots = moving.filter((i) => !movingSet.has(parentIndex(positions, i)));
    const targetDepth = positions[target]?.depth ?? 0;
    // Compute every new depth BEFORE writing any, or a head's shifted depth
    // would be read as the baseline of its own descendants.
    const depths = moving.map((i) => {
      const root = roots.filter((r) => r <= i).pop() ?? i;
      return Math.max(0, positions[i].depth + targetDepth - positions[root].depth);
    });

    const rest = positions.filter((_, i) => !movingSet.has(i));
    const moved = moving.map((i) => positions[i]);
    moved.forEach((draft, k) => (draft.depth = depths[k]));
    rest.splice(target - moving.filter((i) => i < target).length, 0, ...moved);
    this.setPositions(this.applyDepths(rest));
  }

  /** Number a new child would display, e.g. "2.3" under a row numbered "2". */
  private derivedChildNumber(
    positions: readonly PositionDraft[],
    index: number
  ): string {
    return `${displayNumber(positions, index)}.${childCount(positions, index) + 1}`;
  }

  private shiftSubtreeDepth(index: number, delta: number): void {
    const positions = [...this._positions()];
    const end = subtreeEnd(positions, index);
    for (let i = index; i < end; i++)
      positions[i].depth = Math.max(0, positions[i].depth + delta);
    this.setPositions(positions);
  }

  /** Restores the depth invariants IN PLACE, so the drafts stay the same objects. */
  private applyDepths(positions: PositionDraft[]): PositionDraft[] {
    const depths = normalizeDepths(positions);
    positions.forEach((draft, i) => (draft.depth = depths[i]));
    return positions;
  }

  /**
   * Writes the new list and remaps the row selection by draft IDENTITY — every
   * structural change shifts the flat indexes a selection is made of.
   */
  private setPositions(positions: PositionDraft[]): void {
    const selection = this._selection();
    const selected =
      selection?.kind === 'rows'
        ? [...selection.indexes]
            .map((i) => this._positions()[i])
            .filter((draft): draft is PositionDraft => !!draft)
        : [];
    this._positions.set(positions);
    if (selection?.kind !== 'rows') return;
    const indexes = new Set(
      selected.map((draft) => positions.indexOf(draft)).filter((i) => i >= 0)
    );
    this._selection.set(indexes.size ? { kind: 'rows', indexes } : null);
  }

  private nextDraftId(): string {
    return `pos-${++this.draftSequence}`;
  }

  private emptyDraft(
    fields: Partial<Record<PositionField, string>>,
    depth: number
  ): PositionDraft {
    const empty = Object.fromEntries(
      POSITION_FIELDS.map((field) => [field, ''])
    ) as Record<PositionField, string>;
    return {
      id: this.nextDraftId(),
      fields: { ...empty, ...fields },
      raws: {},
      boxes: {},
      discounts: [],
      validation: null,
      page: 1,
      rowbbox: null,
      depth,
    };
  }

  addMetadataField(group: MetadataGroup): void {
    this._metadata.update((metadata) => [
      ...metadata,
      {
        key: `new-${++this.newFieldSequence}`,
        value: '',
        box: null,
        isNew: true,
        label: '',
        group,
      },
    ]);
  }

  // ------------------------------------------------------------- ⌖ marking

  armMark(target: MarkTarget): void {
    this._markTarget.set(target);
  }

  cancelMark(): void {
    this._markTarget.set(null);
  }

  /**
   * A rectangle drawn on the document: reads the covered words as the new
   * field value and stores the box as the field's location.
   */
  assignMarkedBox(box: Box, page: number): string | undefined {
    const target = this._markTarget();
    if (!target) return undefined;
    this._markTarget.set(null);
    const text = textInBox(box, page, this.words);
    if (target.kind === 'position') {
      const draft = this._positions()[target.index];
      if (!draft) return undefined;
      draft.fields[target.field] = text;
      draft.boxes[target.field] = { ...box, page };
    } else {
      const entry = this._metadata().find((e) => e.key === target.key);
      if (!entry) return undefined;
      entry.value = text;
      entry.box = { ...box, page };
    }
    this._revision.update((r) => r + 1);
    return text;
  }

  // ------------------------------------------------------------- plausibility

  /**
   * Check input: the pristine root with the EDITED position list as children
   * and user-entered skonto stages as additional cash-discount entries. The
   * arithmetic itself runs server-side.
   */
  plausibilityInput(): PlausibilityItemInput {
    const root = this.result ? rootItem(this.result) : undefined;
    const positions = this._positions();
    // NESTED, not flat: the endpoint sums a node's DIRECT children and recurses
    // into intermediate ones — a flat list would count every sub-position twice.
    const children = nestPositions<PositionDraft, PlausibilityItemInput>(
      positions,
      (draft, index) => ({
        item_number: draft.fields.item_number || displayNumber(positions, index),
        description: draft.fields.description || null,
        quantity: draft.fields.quantity || null,
        unit_price: draft.fields.unit_price || null,
        net: draft.fields.net || null,
        discount: draft.fields.discount || null,
      })
    );
    const discounts: Adjustment[] = [...(root?.discounts ?? [])];
    const known = discounts
      .filter((d) => d.category === 'cash_discount')
      .map((d) => Number(d.value) * 100)
      .filter((p) => Number.isFinite(p));
    for (const stage of [1, 2]) {
      const percent = parseGermanNumber(this.metadataValue(`cash_discount${stage}`));
      if (
        percent != null &&
        percent > 0 &&
        !known.some((p) => Math.abs(p - percent) <= 0.05)
      ) {
        const days = parseGermanNumber(this.metadataValue(`days${stage}`));
        discounts.push({
          value: String(percent / 100),
          kind: 'percent',
          category: 'cash_discount',
          base: 'gross',
          days: days != null ? `P${days}D` : null,
        });
      }
    }
    return { ...root, items: children, discounts };
  }

  private metadataValue(category: string): string | null {
    const entry = this._metadata().find(
      (e) => e.key === category || e.label === category
    );
    return entry && entry.value !== '' && entry.value != null ? entry.value : null;
  }

  // ------------------------------------------------------------- box lookup

  /** Row box: bbox tightened to its words; else hull of same-page field boxes. */
  private rowBox(draft: PositionDraft | undefined): PageBox | null {
    if (!draft) return null;
    const page = draft.page || 1;
    if (draft.rowbbox)
      return { ...tightenToWords(draft.rowbbox, page, this.words), page };
    // Only boxes of THIS page: a row carried over a page break would otherwise
    // hull coordinates of two pages into one nonsense box.
    const boxes = Object.values(draft.boxes).filter(
      (box): box is PageBox => !!box && (box.page || page) === page
    );
    if (!boxes.length) return null;
    return {
      x0: Math.min(...boxes.map((b) => b.x0)),
      y0: Math.min(...boxes.map((b) => b.y0)),
      x1: Math.max(...boxes.map((b) => b.x1)),
      y1: Math.max(...boxes.map((b) => b.y1)),
      page,
    };
  }

  /**
   * Box of ONE position attribute: the assigned/delivered field box, else the
   * value searched WITHIN the row (so the same text further down can't hit).
   */
  private positionFieldBox(
    draft: PositionDraft | undefined,
    field: PositionField
  ): PageBox | null {
    if (!draft) return null;
    const assigned = draft.boxes[field];
    if (assigned) return assigned;
    const value = draft.fields[field];
    if (!value || !draft.rowbbox) return null;
    const page = draft.page || 1;
    const [x0, y0, x1, y1] = draft.rowbbox;
    const run = matchWordRun(wordsInBox({ x0, y0, x1, y1 }, page, this.words), value);
    return run ? { ...hullOfWords(run), page } : null;
  }

  private metadataFieldBox(entry: MetadataDraft): PageBox | null {
    if (entry.box) return entry.box;
    return findValueBox(entry.value, this.words);
  }

  // ------------------------------------------------------------- build

  private buildFields(item: ExtractionItem): Record<PositionField, string> {
    return Object.fromEntries(
      POSITION_FIELDS.map((field) => {
        if (field === 'discount') return [field, discountsToColumn(item.discounts)];
        const value = item[field];
        return [field, value == null ? '' : String(value)];
      })
    ) as Record<PositionField, string>;
  }

  private buildFieldBoxes(
    item: ExtractionItem
  ): Partial<Record<PositionField, PageBox>> {
    const page = item.page ?? 1;
    const boxes: Partial<Record<PositionField, PageBox>> = {};
    for (const field of POSITION_FIELDS) {
      const bbox = item.field_bboxes[field];
      if (bbox) boxes[field] = { x0: bbox[0], y0: bbox[1], x1: bbox[2], y1: bbox[3], page };
    }
    return boxes;
  }

  private buildMetadataDrafts(
    result: ExtractionResult,
    rawValues: Record<string, string>
  ): MetadataDraft[] {
    const drafts: MetadataDraft[] = [];
    const rootBox = (key: string): PageBox | null => {
      const entry = result.field_bboxes[key];
      if (!entry) return null;
      const [page, [x0, y0, x1, y1]] = entry;
      return { page, x0, y0, x1, y1 };
    };

    for (const group of METADATA_GROUPS) {
      const section = this.sectionAt(result, group);
      const fields =
        group === 'items[0]' ? [...ROOT_METADATA_FIELDS] : Object.keys(section);
      for (const field of fields) {
        const value = (section as Record<string, unknown>)[field];
        if (typeof value === 'boolean' || Array.isArray(value)) continue;
        if (value != null && typeof value === 'object') continue;
        const key = `${group}.${field}`;
        const box = rootBox(key);
        drafts.push({
          key,
          value: value == null ? '' : String(value),
          // Tight value box via word search where possible; the pipeline's own
          // metadata boxes are few and row-wide.
          box: findValueBox(value == null ? '' : String(value), this.words) ?? box,
          raw: rawValues[key],
        });
      }
    }

    // Root adjustments (skonto stages, document discounts): one visible,
    // read-only entry per value (+ term) — the value lives in discounts.
    const root = rootItem(result);
    (root?.discounts ?? []).forEach((adjustment, index) => {
      const halves: ('value' | 'days')[] =
        adjustment.days != null ? ['value', 'days'] : ['value'];
      for (const half of halves) {
        const key = `items[0].discounts[${index}].${half}`;
        const value =
          half === 'days'
            ? String(adjustment.days)
            : isPercentAdjustment(adjustment) && adjustment.value != null
              ? `${fractionToPercentText(adjustment.value)} %`
              : String(adjustment.value ?? '');
        drafts.push({
          key,
          value,
          box: rootBox(key),
          readonly: true,
          adjustment,
          adjustmentIndex: index,
          adjustmentHalf: half,
        });
      }
    });

    return drafts;
  }

  private sectionAt(result: ExtractionResult, group: MetadataGroup): object {
    switch (group) {
      case 'metadata.supplier':
        return result.metadata.supplier;
      case 'metadata.recipient':
        return result.metadata.recipient;
      case 'metadata.receipt':
        return result.metadata.receipt;
      case 'items[0]':
        return rootItem(result) ?? {};
    }
  }
}
